import { FunctionComponent } from 'react';

// libs
import classnames from 'classnames';
import { useRouter } from 'next/router';
import MobileDetect from 'mobile-detect';

// Types
import { GetServerSideProps, InferGetServerSidePropsType } from 'next';

// components
import Layout from '@Pages/tra-cuu/benh/Components/Layout';
import Pagination from '@Components/UI/Pagination';
import DiseaseCard from '@Pages/tra-cuu/benh/Components/DiseaseCard';
import EmptySearch from '@Components/Layout/Search/Components/EmptySearch';

// Contexts
import { useLayoutContext } from '@Components/Layout/Contexts';
import { useDiseasesManager, withDiseasesManager } from '@Pages/tra-cuu/benh/Context/Diseases';
import { getDeviceType, useViewDetected, ViewMedia } from '@Components/Utils/ViewDetected';

// APIs
import diseaseApi from '@Libs/api/cms/disease';
import { getServerSideHttp } from '@Libs/http/cms';

// utils
import { parsePagination } from '@Libs/utils';

// styles
import styles from './Diseases.module.scss';
import { useAppStore } from '@Components/AppStore/Context';

interface DiseaseProps {
    keyword: string;
}

const LIMIT = 36;

const Disease: FunctionComponent<InferGetServerSidePropsType<typeof getServerSideProps> & DiseaseProps> = () => {
    const router = useRouter();
    const { setLoading } = useAppStore();
    const { deviceType } = useViewDetected();
    const { onScroll } = useLayoutContext();
    const { total, keyword, diseases, hasMore, loadMore } = useDiseasesManager();

    const isMobile = ['mobile', 'tablet'].includes(deviceType);

    const className = classnames([
        styles.wrapper,
        {
            [styles.mobile]: isMobile,
        },
    ]);

    const handleSearch = (keyword) => {
        router.push({
            pathname: '/tra-cuu/benh',
            query: {
                s: keyword,
            },
        });
    };

    const handleLoadMore = async () => {
        await loadMore({
            ...(keyword && { _q: keyword }),
            _limit: LIMIT,
            _start: diseases.length,
        });
    };

    onScroll(
        async (event, { isBottom }) => {
            if (isMobile && isBottom) {
                await handleLoadMore();
            }

            return () => setLoading(false);
        },
        [diseases],
        ({ isBottom }) => isMobile && isBottom && hasMore && setLoading(true),
    );

    return (
        <Layout onSearch={handleSearch} keyword={keyword}>
            <div className={className}>
                {total > 0 && (
                    <DiseaseCard diseases={diseases}>
                        <ViewMedia is={'desktop'}>{<Pagination total={total} limit={LIMIT} />}</ViewMedia>
                    </DiseaseCard>
                )}
                {total === 0 && (
                    <div className={styles.empty_wrapper}>
                        <EmptySearch withoutSearch title="Tra cứu loại bệnh" src={'/assets/tra-cuu/img_search_empty.png'} subTitle="Xin lỗi chúng tôi không tìm thấy kết quả phù hợp với bạn." />
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default withDiseasesManager<DiseaseProps>(Disease)({ limit: LIMIT });

export const getServerSideProps: GetServerSideProps = async (ctx) => {
    const http = getServerSideHttp(ctx);

    const userDetected = new MobileDetect(ctx.req.headers['user-agent']);
    const deviceType = getDeviceType(userDetected);

    const isMobile = ['mobile', 'tablet'].includes(deviceType);

    const alphabet = ctx?.query?.alphabet || '';

    const page = isMobile ? 1 : Number(ctx?.query?.page || 1);

    const { _start, _limit } = parsePagination(page, LIMIT);

    if (alphabet) {
        const { count = 0, diseases = [] } = await diseaseApi(http).findOneDiseaseByAlphabet(alphabet.toString(), { _start, _limit });

        return {
            props: {
                page,
                alphabet,
                diseases,
                total: count,
            },
        };
    }

    const keyword = ctx?.query?.s || '';

    const { count = 0, diseases = [] } = await diseaseApi(http).pagination(
        {
            ...(keyword && { _q: keyword }),
        },
        _start,
        _limit,
    );

    return {
        props: {
            page,
            keyword,
            diseases,
            total: count,
        },
    };
};
